import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ClientData,
  MouilletteScores,
  Preferences,
  Recommendation,
  SkinMeasures,
  StoreId,
} from '@/types';

interface SessionState {
  // Contexto fijo de la tablet
  storeId: StoreId | null;
  sellerId: string | null;
  setContext: (storeId: StoreId, sellerId: string) => void;

  // Consulta en curso
  consultationId: string | null;
  startedAt: string | null;
  client: Partial<ClientData>;
  skin: Partial<SkinMeasures>;
  preferences: Partial<Preferences>;
  mouillettes: Partial<MouilletteScores>;
  recommendations: Recommendation[];
  couponCode: string | null;

  startConsultation: () => void;
  setClient: (data: Partial<ClientData>) => void;
  setSkin: (data: Partial<SkinMeasures>) => void;
  setPreferences: (data: Partial<Preferences>) => void;
  setMouillettes: (data: Partial<MouilletteScores>) => void;
  setRecommendations: (recs: Recommendation[], couponCode: string) => void;
  resetConsultation: () => void;
}

const newId = () =>
  // 8 chars alfanuméricos en minúscula — corto pero seguro contra colisiones a corto plazo
  Math.random().toString(36).slice(2, 6) + Date.now().toString(36).slice(-4);

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      storeId: null,
      sellerId: null,
      setContext: (storeId, sellerId) => set({ storeId, sellerId }),

      consultationId: null,
      startedAt: null,
      client: {},
      skin: {},
      preferences: {},
      mouillettes: {},
      recommendations: [],
      couponCode: null,

      startConsultation: () =>
        set({
          consultationId: newId(),
          startedAt: new Date().toISOString(),
          client: {},
          skin: {},
          preferences: {},
          mouillettes: {},
          recommendations: [],
          couponCode: null,
        }),
      setClient: (data) => set((s) => ({ client: { ...s.client, ...data } })),
      setSkin: (data) => set((s) => ({ skin: { ...s.skin, ...data } })),
      setPreferences: (data) => set((s) => ({ preferences: { ...s.preferences, ...data } })),
      setMouillettes: (data) => set((s) => ({ mouillettes: { ...s.mouillettes, ...data } })),
      setRecommendations: (recommendations, couponCode) => set({ recommendations, couponCode }),
      resetConsultation: () =>
        set({
          consultationId: null,
          startedAt: null,
          client: {},
          skin: {},
          preferences: {},
          mouillettes: {},
          recommendations: [],
          couponCode: null,
        }),
    }),
    {
      name: 'elixir-olfativa-session',
      // Persistimos también la consulta en curso para que F5 no rompa
      // y para soporte offline (objetivo del producto).
      partialize: (s) => ({
        storeId: s.storeId,
        sellerId: s.sellerId,
        consultationId: s.consultationId,
        startedAt: s.startedAt,
        client: s.client,
        skin: s.skin,
        preferences: s.preferences,
        mouillettes: s.mouillettes,
        recommendations: s.recommendations,
        couponCode: s.couponCode,
      }),
    }
  )
);
